// Media stays on device, not in a module-wide RAM cache. IndexedDB failures
// must never prevent opening a file from the network.
const DB_NAME = "alef-chat-media-v1";
const PURGE_FLAG = "alef:chat-media-purge-pending";
export const MEDIA_LIMIT_BYTES = 150 * 1024 * 1024;
let epoch = 0;
let writes = Promise.resolve();
export const mediaCacheEpoch = () => epoch;
export function mediaScope(role) {
  const key = { student: "student_phone", guest: "guest_phone", parent: "parent_phone",
    advisor: "advisor_id", supporter: "supporter_id", supervisor: "supervisor_id", admin: "admin_id" }[role];
  return key && localStorage.getItem(key) ? role + ":" + localStorage.getItem(key) : "";
}
const cacheKey = (scope, url) => scope + "|" + new URL(url, window.location.origin).href;
const result = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});
function open() {
  if (!window.indexedDB) return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore("meta", { keyPath: "key" });
      request.result.createObjectStore("files", { keyPath: "key" });
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      // Logout navigation may interrupt asynchronous clearing. A durable flag
      // makes the next page clear storage before it can read an old blob.
      if (localStorage.getItem(PURGE_FLAG)) {
        const tx = db.transaction(["meta","files"], "readwrite");
        tx.objectStore("meta").clear(); tx.objectStore("files").clear();
        tx.oncomplete = () => { localStorage.removeItem(PURGE_FLAG); resolve(db); };
        tx.onerror = tx.onabort = () => { db.close(); resolve(null); };
      } else resolve(db);
    };
    request.onerror = () => reject(request.error);
    request.onblocked = () => resolve(null);
  });
}
const done = (tx) => {
  const promise = new Promise((resolve, reject) => {
    tx.oncomplete = resolve; tx.onabort = tx.onerror = () => reject(tx.error || new Error("storage unavailable"));
  });
  promise.catch(() => {}); // request failures may reject before the final await
  return promise;
};
export async function readChatMedia(scope, url) {
  if (!scope) return null;
  let db;
  try {
    db = await open(); if (!db) return null;
    const key = cacheKey(scope, url), tx = db.transaction(["meta","files"], "readwrite"), completed = done(tx);
    const file = await result(tx.objectStore("files").get(key));
    if (file) tx.objectStore("meta").put({ key, scope, size: file.blob.size, used: Date.now() });
    await completed;
    return file?.blob || null;
  } catch (_) { return null; } finally { db?.close(); }
}
export function storeChatMedia(scope, url, blob, expectedEpoch = epoch) {
  const run = async () => {
    if (!scope || !blob?.size || blob.size > MEDIA_LIMIT_BYTES || expectedEpoch !== epoch) return;
    let db;
    try {
      db = await open(); if (!db || expectedEpoch !== epoch) return;
      const tx = db.transaction(["meta","files"], "readwrite"), completed = done(tx);
      const meta = tx.objectStore("meta"), files = tx.objectStore("files");
      const entries = await result(meta.getAll());
      const key = cacheKey(scope, url);
      let bytes = entries.filter((item) => item.key !== key).reduce((sum, item) => sum + item.size, 0);
      for (const entry of entries.filter((item) => item.key !== key).sort((a,b) => a.used - b.used)) {
        if (bytes + blob.size <= MEDIA_LIMIT_BYTES) break;
        meta.delete(entry.key); files.delete(entry.key); bytes -= entry.size;
      }
      if (expectedEpoch !== epoch) { tx.abort(); await completed; return; }
      files.put({ key, blob }); meta.put({ key, scope, size: blob.size, used: Date.now() });
      await completed;
    } catch (_) { /* Quota/private mode: the downloaded file still opens. */ }
    finally { db?.close(); }
  };
  writes = writes.then(run, run);
  return writes;
}
export function clearChatMedia(scope = null) {
  epoch += 1;
  const run = async () => {
    let db;
    try {
      db = await open(); if (!db) return;
      const tx = db.transaction(["meta","files"], "readwrite"), completed = done(tx);
      const entries = await result(tx.objectStore("meta").getAll());
      entries.filter((item) => !scope || item.scope === scope).forEach((entry) => {
        tx.objectStore("meta").delete(entry.key); tx.objectStore("files").delete(entry.key);
      });
      await completed;
    } catch (_) {} finally { db?.close(); }
  };
  writes = writes.then(run, run);
  return writes;
}
export function purgeChatMediaOnLogout() {
  localStorage.setItem(PURGE_FLAG, "1");
  void clearChatMedia();
}
export async function chatMediaUsage(scope) {
  let db;
  try {
    db = await open(); if (!db) return 0;
    const tx = db.transaction("meta"), completed = done(tx);
    const entries = await result(tx.objectStore("meta").getAll()); await completed;
    return entries.filter((item) => item.scope === scope).reduce((sum, item) => sum + item.size, 0);
  } catch (_) { return 0; } finally { db?.close(); }
}
